<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { TRACK_KIND, VIDEO_CALL_NETWORK_QUALITY, type streamsStatus } from "../definitions";

import Notification from "./Notification.vue";

import { hasValue } from "@/Utils/Objects";
import useAppStore from "@/Modules/App/Store";

export interface NotificationsProps {
  remoteName: string;
  streamsStatus: streamsStatus;
  networkQuality: VIDEO_CALL_NETWORK_QUALITY;
}

const { t } = useI18n();
const appStore = useAppStore();

const emit = defineEmits<{
  (event: "closeAction", value: TRACK_KIND): void;
}>();

const props = defineProps<NotificationsProps>();

const getPositionStyle = (elementId: string) => {
  if (appStore.isMobileViewport) return {};

  const videoWrapper = document.getElementById("video-call-wrapper")?.getBoundingClientRect();
  const element = document.getElementById(elementId)?.getBoundingClientRect();

  if (!hasValue(videoWrapper) || !hasValue(element)) return;
  if (element.top > videoWrapper.height) return;

  return { left: `${Math.max(element.left - 60, 16)}px`, right: "auto" };
};
</script>

<template>
  <Notification
    class="notification notification--share_screen"
    :value="props.streamsStatus.SHARE_SCREEN.video"
    :showValue="undefined"
  >
    <span />
    {{ t("chat.message.video_call.share_screen_not_supported") }}
  </Notification>

  <Notification
    class="notification notification--remote--video"
    :value="props.streamsStatus.REMOTE.video"
    :showValue="false"
  >
    <span />
    {{ t("chat.message.video_call.video_stop_remote", { name: remoteName }) }}
  </Notification>

  <Notification
    class="notification notification--remote--audio"
    :value="props.streamsStatus.REMOTE.audio"
    :showValue="false"
  >
    <span />
    {{ t("chat.message.video_call.audio_stop_remote", { name: remoteName }) }}
  </Notification>

  <Notification
    v-if="
      ![VIDEO_CALL_NETWORK_QUALITY.LOWEST, VIDEO_CALL_NETWORK_QUALITY.LOW].includes(props.networkQuality) ||
      props.streamsStatus.LOCAL.isUserVideoAction
    "
    class="notification notification--local--video"
    :value="props.streamsStatus.LOCAL.video"
    :showValue="false"
    :style="getPositionStyle('call_camera')"
    @close="
      () => {
        if (props.streamsStatus.LOCAL.isUserVideoAction) {
          emit('closeAction', TRACK_KIND.VIDEO);
        }
      }
    "
  >
    {{ t("chat.message.video_call.video_stop") }}
  </Notification>

  <template v-else-if="!props.streamsStatus.LOCAL.isUserVideoAction">
    <Notification
      class="notification notification--local--video"
      :value="props.streamsStatus.LOCAL.video"
      :showValue="false"
      :style="getPositionStyle('call_camera')"
      @close="
        () => {
          if (props.streamsStatus.LOCAL.isUserVideoAction) {
            emit('closeAction', TRACK_KIND.VIDEO);
          }
        }
      "
    >
      {{ t("chat.message.video_call.video_stop_forced") }}
    </Notification>

    <Notification
      class="notification notification--local--video"
      :value="props.streamsStatus.LOCAL.video"
      :showValue="true"
      :style="getPositionStyle('call_camera')"
    >
      {{ t("chat.message.video_call.video_start_forced") }}
    </Notification>
  </template>

  <Notification
    class="notification notification--local--audio"
    :value="props.streamsStatus.LOCAL.audio"
    :showValue="false"
    :style="getPositionStyle('call_audio')"
  >
    {{ t("chat.message.video_call.audio_stop") }}
  </Notification>

  <Notification
    class="notification notification--low_conection"
    :value="props.networkQuality"
    :showValue="VIDEO_CALL_NETWORK_QUALITY.LOW"
  >
    {{ t("chat.message.video_call.low_quality") }}
  </Notification>

  <Notification
    class="notification notification--lowest_conection"
    :value="props.networkQuality"
    :showValue="VIDEO_CALL_NETWORK_QUALITY.LOWEST"
  >
    {{ t("chat.message.video_call.lowest_quality") }}
  </Notification>
</template>

<style scoped lang="scss">
$notification_height: 32px;
$notification_spacing: calc($notification_height + $space-4);

.notification {
  @include sp.padding(false, 2);
  @include text.general(low, s);
  @include colors.color(content, inverse);
  @include colors.bg(background, overlay);
  @include radius(s);

  position: absolute;
  z-index: 3;
  cursor: pointer;
  left: 50%;
  transform: translateX(-50%);
  height: $notification_height;
  width: max-content;
  max-width: 90%;

  &--share_screen {
    top: 50%;

    span {
      top: auto !important;
      bottom: -2px;
    }
  }

  &--remote--video,
  &--remote--audio,
  &--share_screen {
    span {
      @include colors.bg(background, overlay);

      height: 4px;
      width: 4px;
      position: absolute;
      top: -2px;
      left: 50%;
      transform: rotate(45deg);
    }
  }

  &--remote--video {
    top: 55%;
  }

  &--remote--audio {
    top: calc(55% + $notification_spacing);
  }

  &--low_conection {
    top: calc(50% - $notification_spacing);
  }

  &--lowest_conection {
    top: calc(50% - (2 * $notification_spacing));
  }

  &--local--video,
  &--local--audio {
    bottom: 90px;
  }
}

@media (min-width: $breakpoint-m) {
  .notification {
    &--local--video,
    &--local--audio {
      bottom: 90px;
      transform: none !important;
    }
  }
}
</style>
